// ActivityTracker.jsx
// Background activity tracking component
// - Starts a session on mount, heartbeats every 5 min, detects idle at 15 min
// - Tracks page visits when `currentPage` prop changes
// - Shows session timer in a small bar (transparent to user)
// - Exposes `logAction(type, details)` via window for other components to call
//
// Usage in CRM: <ActivityTracker currentUser={currentUser} currentPage={page} />
// Action logging: window.ttpLogAction?.('lead_updated', 'Changed status to Contacted')

import React, { useState, useEffect, useRef, useCallback } from 'react';

const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;
const HEARTBEAT_MS = 5 * 60 * 1000; // 5 minutes
const IDLE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes

const ActivityTracker = ({ currentUser, currentPage }) => {
  const [sessionId, setSessionId] = useState(null);
  const [activeMinutes, setActiveMinutes] = useState(0);
  const [isIdle, setIsIdle] = useState(false);
  const [sessionStart, setSessionStart] = useState(null);
  const lastActivity = useRef(Date.now());
  const heartbeatInterval = useRef(null);
  const idleCheckInterval = useRef(null);
  const currentPageRef = useRef(currentPage);
  const sessionIdRef = useRef(null);

  // Skip tracking for installers (they use the Crew App)
  const shouldTrack = currentUser && (currentUser.role === 'admin' || currentUser.role === 'owner');

  // ── Start session ──
  const startSession = useCallback(async () => {
    if (!currentUser) return;
    try {
      const res = await fetch(`${BASE_URL}/api/activity/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'start-session',
          userId: currentUser.id,
          userName: currentUser.name,
          role: currentUser.role,
        }),
      });
      const data = await res.json();
      if (data.sessionId) {
        setSessionId(data.sessionId);
        sessionIdRef.current = data.sessionId;
        setSessionStart(Date.now());
        setActiveMinutes(0);
        console.log('[activity] Session started:', data.sessionId);
      }
    } catch (e) {
      console.log('[activity] Session start failed:', e.message);
    }
  }, [currentUser]);

  // ── Heartbeat ──
  const sendHeartbeat = useCallback(async () => {
    if (!sessionIdRef.current) return;
    const idle = Date.now() - lastActivity.current > IDLE_THRESHOLD_MS;
    setIsIdle(idle);

    try {
      const res = await fetch(`${BASE_URL}/api/activity/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'heartbeat',
          sessionId: sessionIdRef.current,
          idle,
          currentPage: currentPageRef.current,
        }),
      });
      const data = await res.json();
      if (data.activeMinutes !== undefined) {
        setActiveMinutes(data.activeMinutes);
      }
    } catch (e) { /* silent */ }
  }, []);

  // ── End session ──
  const endSession = useCallback(async (reason = 'logout') => {
    if (!sessionIdRef.current) return;
    try {
      await fetch(`${BASE_URL}/api/activity/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'end-session',
          sessionId: sessionIdRef.current,
          reason,
        }),
      });
      console.log('[activity] Session ended:', reason);
    } catch (e) { /* silent */ }
    setSessionId(null);
    sessionIdRef.current = null;
  }, []);

  // ── Page visit tracking ──
  const trackPageVisit = useCallback(async (page) => {
    if (!sessionIdRef.current || !page) return;
    try {
      await fetch(`${BASE_URL}/api/activity/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'page-visit',
          sessionId: sessionIdRef.current,
          userId: currentUser?.id,
          page,
        }),
      });
    } catch (e) { /* silent */ }
  }, [currentUser]);

  // ── Action logging (exposed via window) ──
  const logAction = useCallback(async (actionType, details) => {
    if (!sessionIdRef.current) return;
    try {
      await fetch(`${BASE_URL}/api/activity/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'log-action',
          sessionId: sessionIdRef.current,
          userId: currentUser?.id,
          actionType,
          details,
          currentPage: currentPageRef.current,
        }),
      });
    } catch (e) { /* silent */ }
  }, [currentUser]);

  // ── Initialize ──
  useEffect(() => {
    if (!shouldTrack) return;

    startSession();

    // Activity listeners
    const onActivity = () => { lastActivity.current = Date.now(); };
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('click', onActivity);
    window.addEventListener('scroll', onActivity);
    window.addEventListener('touchstart', onActivity);

    // Heartbeat interval
    heartbeatInterval.current = setInterval(sendHeartbeat, HEARTBEAT_MS);

    // Local timer update (every 60s for display)
    idleCheckInterval.current = setInterval(() => {
      const idle = Date.now() - lastActivity.current > IDLE_THRESHOLD_MS;
      setIsIdle(idle);
      if (!idle && sessionStart) {
        // Rough local estimate between heartbeats
        setActiveMinutes(prev => prev + 1);
      }
    }, 60000);

    // Expose logAction and endSession globally
    window.ttpLogAction = logAction;
    window.ttpEndSession = endSession;

    // Cleanup on unmount / logout
    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('click', onActivity);
      window.removeEventListener('scroll', onActivity);
      window.removeEventListener('touchstart', onActivity);
      clearInterval(heartbeatInterval.current);
      clearInterval(idleCheckInterval.current);
      delete window.ttpLogAction;
      delete window.ttpEndSession;
      if (sessionIdRef.current) {
        // Fire-and-forget end session
        navigator.sendBeacon?.(`${BASE_URL}/api/activity/track`, JSON.stringify({
          action: 'end-session', sessionId: sessionIdRef.current, reason: 'unmount',
        }));
      }
    };
  }, [shouldTrack, startSession, sendHeartbeat, logAction, endSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track page changes ──
  useEffect(() => {
    if (currentPage && currentPage !== currentPageRef.current) {
      currentPageRef.current = currentPage;
      trackPageVisit(currentPage);
    }
  }, [currentPage, trackPageVisit]);

  // ── End session on beforeunload ──
  useEffect(() => {
    if (!shouldTrack) return;
    const onUnload = () => {
      if (sessionIdRef.current) {
        navigator.sendBeacon?.(`${BASE_URL}/api/activity/track`, JSON.stringify({
          action: 'end-session', sessionId: sessionIdRef.current, reason: 'tab_close',
        }));
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [shouldTrack]);

  // Don't render anything for installers or if not tracked
  if (!shouldTrack || !sessionId) return null;

  // ── Timer display ──
  const hours = Math.floor(activeMinutes / 60);
  const mins = activeMinutes % 60;
  const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      fontSize: 11, color: isIdle ? 'var(--stone-400)' : 'var(--accent-green)',
      fontWeight: 600, padding: '0 8px',
      transition: 'color 0.3s',
    }}>
      <div style={{
        width: 6, height: 6, borderRadius: '50%',
        background: isIdle ? 'var(--stone-400)' : 'var(--accent-green)',
        animation: isIdle ? 'none' : 'pulse 2s ease-in-out infinite',
      }} />
      <span>{isIdle ? 'Idle' : 'Active'}: {timeStr}</span>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
    </div>
  );
};

export default ActivityTracker;
